// The AGM RSVP, as members see it in the app: the RSVP page (/agm), the EM briefing
// (/agm/briefing) and the "AGM" row on the Me page. Flag name, routes, thresholds and
// EVERY visible string live here and nowhere else, as the BF2026 pledge's do, so the
// copy can be reviewed (and changed after corpsec's review) in one file. The copy is
// the "Copy deck" of the agreed UI, and the contract with the API is §4.2 of
// docs/plans/2026-10-22-agm-rsvp-mobile-migration.md.
//
// Create the `agm-rsvp` row off in production and flip it when the year's RSVP opens;
// delete the flag, and its guard, once the feature is rolled out for good.

import { PRIVATE_BLOCK_ATTR } from '@/constants/replay-privacy';

export const AGM_RSVP_FLAG = 'agm-rsvp';

export const AGM_ROUTES = Object.freeze({
  rsvp: '/agm',
  briefing: '/agm/briefing',
  // Dev builds only (src/router/index.js): the fake API and the state picker.
  harness: '/agm-states',
});

// Where a guard sends a member who opens an AGM link while the flag is off.
export const AGM_FLAG_OFF_PATH = '/tabs/home';

// Under VUE_APP_API_URL (which already ends in /api): GET returns the whole screen,
// PUT /rsvp answers with the same body.
export const AGM_API_PATH = '/v2/agm';

// Who the contact row writes to when the API sends none (a member who is not
// eligible gets no `links`). The API's own address wins when it has one.
export const AGM_ENQUIRIES_EMAIL = 'corpsec@heartofgodchurch.org';

// What the page can be, derived by the store's `view` getter from one API response.
export const AGM_VIEWS = Object.freeze({
  loading: 'loading',
  error: 'error',
  ineligible: 'ineligible',
  none: 'none',
  ended: 'ended',
  closed: 'closed',
  open: 'open',
  answered: 'answered',
});

// Why the last save did not land. `closed` is the API's 409: the cutoff passed while
// the page was open.
export const AGM_SAVE_ERRORS = Object.freeze({ failed: 'failed', closed: 'closed' });

// The deadline text turns amber this many calendar days (Singapore) before the cutoff,
// and on the last day it says "tonight".
export const AGM_DEADLINE_SOON_DAYS = 3;

// The dark event header. Capacitor's StatusBar takes a literal colour, so this repeats
// tailwind.config.js's `brown-900` (tests/unit/utils/agm-constants.spec.js fails if the
// two ever differ).
export const AGM_STATUS_BAR_COLOR = '#322A21';

// Mixpanel autocapture: no clicks, inputs, scrolls or page views are captured on the
// AGM routes. The buttons' own text is the member's answer, and a recorded click on
// "Add to calendar" or Babyspace says they are attending. Matched against the full
// URL (`https://host/agm`, `capacitor://localhost/agm/briefing?x`), so the path is
// anchored after the origin: `/agm-states` and `/courses/agm` do not match.
// Read by ensureMixpanelInitialized in src/utils/mixpanel.js.
export const AGM_AUTOCAPTURE_BLOCK_URL_REGEXES = Object.freeze([
  /^[a-z][a-z0-9+.-]*:\/\/[^/]*\/agm(?:[/?#]|$)/i,
]);

// Datadog RUM names a click after the clicked element's text ("Yes, I'll be there").
// An ancestor's `data-dd-action-name` overrides that (the SDK uses `closest()`), so
// every AGM page and sheet carries this, and every click on them is just "AGM".
export const AGM_ACTION_NAME_ATTRS = Object.freeze({ 'data-dd-action-name': 'AGM' });

// The visible strings. Functions take already-formatted pieces (see utils/agm-format.js).
export const AGM_COPY = Object.freeze({
  loading: 'Loading',

  // Titles
  pageTitle: 'Annual General Meeting',
  briefingTitle: 'EM briefing',

  // A · open, not answered
  question: 'Will you be attending?',
  yes: "Yes, I'll be there",
  no: "No, I won't be there",
  changeUntil: deadline => `You can change this until ${deadline}.`,
  saveFailed: "We couldn't save your answer. Check your connection and try again.",
  closedWhileSaving: 'RSVP closed before we could save your answer.',

  // B · answered
  attending: 'Attending',
  notAttending: 'Not attending',
  changeRsvp: 'Change RSVP',
  changeBy: deadline => `by ${deadline}`,

  // C · closed. The date sentence is left out when the CMS has no deadline.
  closedAnswered: date =>
    date
      ? `RSVP closed on ${date}. To change your answer, email corpsec.`
      : 'RSVP has closed. To change your answer, email corpsec.',
  didntRsvp: "You didn't RSVP",
  closedNone: date =>
    date
      ? `RSVP closed on ${date}. If you'd still like to attend, email corpsec.`
      : "RSVP has closed. If you'd still like to attend, email corpsec.",
  closedNoneRace: "If you'd still like to attend, email corpsec.",

  // After the AGM
  ended: 'This AGM has ended',
  endedOn: date => `It took place on ${date}.`,
  endedCardLabel: year => `AGM ${year}`,

  // D, E, F
  noAgmTitle: 'No AGM to RSVP for yet',
  noAgmBody: "When the next AGM is announced, you'll be able to RSVP here.",
  ineligibleTitle: 'RSVP is for eligible members',
  ineligibleBody:
    "Only members on this year's AGM list can RSVP. If you think you should be on it, email corpsec.",
  loadFailedTitle: "We couldn't load the AGM",
  loadFailedBody: 'Check your connection and try again.',
  tryAgain: 'Try again',

  // Before the AGM, and the contact row
  beforeTitle: 'Before the AGM',
  briefingRow: 'Watch the EM briefing',
  briefingRowOpen: 'Video · watch before you decide',
  briefingRowAnswered: 'Video · what the AGM will cover',
  babyspaceRow: 'Bringing a baby?',
  babyspaceRowSub: 'Request a seat in Babyspace',
  contactRow: 'Questions? Email corpsec',

  // Calendar and the header's pill
  addToCalendar: 'Add to calendar',
  calendarSheetTitle: 'Add to calendar',
  calendarSheetSubtitle: (name, when) => `${name} · ${when}`,
  calendarFile: 'Apple Calendar or Outlook',
  calendarFileSub: 'Saves a calendar file',
  calendarGoogle: 'Google Calendar',
  calendarGoogleSub: 'Opens in your browser',
  cancel: 'Cancel',
  calendarFailed: "We couldn't save the calendar file. Try Google Calendar instead.",

  // Change sheet
  changeSheetTitle: 'Change your RSVP',
  current: 'Current',
  updateRsvp: 'Update RSVP',

  // Briefing page
  briefingWatchBefore: date => `Watch before the AGM on ${date}.`,
  briefingNotRsvpd: "You haven't RSVP'd yet",
  rsvpNow: 'RSVP now',
  briefingUnavailableTitle: "The briefing isn't available",
  briefingUnavailableBody: 'If you were expecting it, email corpsec.',
  briefingVideoTitle: 'EM briefing video',

  // Me page
  meRow: 'AGM',

  // Screen-reader announcement once a save lands (the card changes in place; there
  // is no toast).
  savedAnnouncement: attending => `RSVP saved: ${attending ? 'attending' : 'not attending'}`,
});

// Answer elements (the Yes / No buttons, the status line, the sheets' radio cards)
// are recorded by Mixpanel replay as a placeholder box, not as the element. See
// constants/replay-privacy.js, which owns the attribute.
export const AGM_PRIVATE_BLOCK_ATTRS = Object.freeze({ [PRIVATE_BLOCK_ATTR]: '' });
